import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as walletService from 'services/wallet';
import { addWallet } from 'store/actions/wallet';

const COST_SCHEDULE = [
  { min: 0, max: Number.MAX_SAFE_INTEGER, prices: { NGN: 1, KES: 0.08, GHS: 0.0098, USD: 0.00083 } }
];

export const useWalletService = () => {
  const dispatch = useDispatch();
  const { tenant_id, token } = useSelector((state) => state.user_data);

  const checkBalanceSufficience = async () => {
    const { error, payload } = await walletService.checkCreditsLevel({ token });
    if (error) return toast.error(error);
    const { is_sufficient } = payload;
    return is_sufficient;
  };

  const createWallet = async () => {
    const { error, payload } = await walletService.create({ data: { tenant_id }, token });
    if (error) return toast.error('Could not retrieve wallet. Please reload page');
    return dispatch(
      addWallet({
        ...payload,
        balance: payload.balance ? payload.balance.toFixed(2) : 0
      })
    );
  };

  const createVirtualAccount = async (data) => {
    const { error, payload } = await walletService.createVirtualAccount({ data, token });
    if (error) {
      toast.error(error);
      return null;
    }

    updateWalletInStore({ virtual_account: { ...payload } });
    toast.success('Virtual account created successfully');
    return payload;
  };

  const determineTopUpCostSummary = ({
    credits_to_buy = 0,
    currency = 'NGN',
    has_subscription = false,
    tax_rate = 0,
    wallet
  }) => {
    let debt = 0;
    if (wallet.debt) {
      debt = wallet.debt.amount || 0;
    }

    const { prices } = COST_SCHEDULE[0];
    const base_rate = has_subscription ? prices[currency] * 0.85 : prices[currency];
    const rate = wallet.rate || base_rate;
    const amount = Math.round(credits_to_buy * rate);
    const total_amount = amount + Number(debt);
    const tax = (Number(total_amount) * tax_rate) / 100;

    return { amount, debt, tax, total_amount };
  };

  const fetchWallet = async () => {
    const query_string = `tenant_id=${tenant_id}`;
    const { error, payload } = await walletService.read({ token, query_string });
    if (error) return toast.error(error);

    const {
      data: [wallet]
    } = payload;

    if (wallet && wallet.id) {
      return dispatch(
        addWallet({
          ...wallet,
          balance: wallet.balance ? wallet.balance.toFixed(2) : 0
        })
      );
    }

    createWallet();
  };

  const formatBalance = (count = 0) => {
    const integer_value = Number(count);

    if (integer_value > 9999 && integer_value <= 999999) {
      return `${(integer_value / 1000).toFixed(2)}K`;
    }

    if (integer_value > 999999) {
      return `${(integer_value / 1000000).toFixed(2)}M`;
    }

    return integer_value;
  };

  const updateWallet = async (data) => {
    const { payload } = await walletService.update({ token, data });
    return !!payload;
  };

  const updateWalletInStore = (data) => {
    dispatch(updateWallet({ ...data }));
  };

  return {
    checkBalanceSufficience,
    createVirtualAccount,
    createWallet,
    determineTopUpCostSummary,
    fetchWallet,
    formatBalance,
    updateWallet,
    updateWalletInStore
  };
};
